import React from 'react';
import { socialLinks, contactInfo } from '../data/mockData';
import { Instagram, Facebook, Send, Phone, Mail, MapPin } from 'lucide-react';
import { Separator } from './ui/separator';
import { useLocation, useNavigate } from 'react-router-dom';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      return;
    }

    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contact" className="bg-gradient-to-b from-amber-900 to-amber-950 text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_1d505a5f-a544-4c6e-8862-fec39bdf81d8/artifacts/gj3rnkah_image.png"
                alt="Ser Tão Doce"
                className="w-16 h-16 object-contain bg-white rounded-full p-2"
              />
              <div>
                <div className="font-bold text-xl">Ser Tão Doce</div>
                <div className="text-sm text-amber-300">Waleria Mapeli</div>
              </div>
            </div>
            <p className="text-amber-200 text-sm leading-relaxed">
              Doces artesanais portugueses feitos com amor e dedicação para tornar seus momentos ainda mais especiais.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="font-bold text-lg mb-4">Navegação</h3>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => scrollToSection('menu')}
                  className="text-amber-200 hover:text-white transition-colors text-sm"
                >
                  Cardápio
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('pricing')}
                  className="text-amber-200 hover:text-white transition-colors text-sm"
                >
                  Preços
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="text-amber-200 hover:text-white transition-colors text-sm"
                >
                  Sobre Nós
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('gallery')}
                  className="text-amber-200 hover:text-white transition-colors text-sm"
                >
                  Galeria
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('pricing')}
                  className="text-amber-200 hover:text-white transition-colors text-sm"
                >
                  Fazer Pedido
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-lg mb-4">Contato</h3>
            <ul className="space-y-3">
              <li>
                <a 
                  href={socialLinks.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-amber-200 hover:text-white transition-colors text-sm"
                >
                  <Phone className="w-4 h-4" />
                  {contactInfo.phone}
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-2 text-amber-200 hover:text-white transition-colors text-sm"
                >
                  <Mail className="w-4 h-4" />
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-2 text-amber-200 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{contactInfo.address}</span>
              </li>
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="font-bold text-lg mb-4">Redes Sociais</h3>
            <div className="flex flex-wrap gap-3">
              <a 
                href={socialLinks.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-amber-800 hover:bg-amber-700 flex items-center justify-center transition-colors"
                aria-label="TikTok"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="w-5 h-5 fill-current">
                  <path d="M14.2 3h2.7c.2 1.6 1.1 2.9 2.5 3.8-1.3 0-2.5-.4-3.5-1.1v7.7c0 3-2.5 5.5-5.5 5.5S5 16.4 5 13.4s2.5-5.5 5.5-5.5c.3 0 .7 0 1 .1v2.7c-.3-.1-.7-.2-1-.2-1.6 0-2.9 1.3-2.9 2.9s1.3 2.9 2.9 2.9 2.9-1.3 2.9-2.9V3z" />
                </svg>
              </a>
              <a 
                href={socialLinks.threads}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-amber-800 hover:bg-amber-700 flex items-center justify-center transition-colors"
                aria-label="Threads"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="w-5 h-5 fill-current">
                  <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.5 12.068v-.04c.024-3.474.9-6.322 2.606-8.463C5.843 1.424 8.186.204 11.297.015c2.373-.144 4.384.49 5.977 1.885.998.884 1.768 2.046 2.293 3.458l-1.867.806c-.388-1.067-.958-1.95-1.695-2.622-1.217-1.067-2.768-1.546-4.609-1.434-2.578.16-4.47 1.155-5.787 2.959C4.413 6.594 3.69 9.016 3.668 12.04v.04c.021 3.057.734 5.483 2.12 7.215C7.135 21.124 9.037 22.12 11.617 22.14c2.39.016 3.97-.604 5.229-1.955.82-.887 1.33-2.054 1.556-3.458l1.915.322c-.282 1.783-.962 3.265-2.04 4.437-1.577 1.705-3.722 2.53-6.091 2.514zm5.09-11.09c-.444-2.983-2.38-4.764-5.377-4.965-1.63-.11-3.077.36-4.147 1.357-.956.896-1.44 2.115-1.374 3.437.067 1.338.633 2.427 1.64 3.152.871.626 2.038.962 3.374.975.09.001.178.002.266.002 2.12 0 3.79-.694 4.963-2.065.793-.928 1.15-2.068.655-1.893zm-1.888.316c-.81.947-2.046 1.47-3.63 1.47-.063 0-.127 0-.19-.002-.958-.01-1.773-.24-2.358-.665-.554-.398-.854-1.022-.9-1.851-.045-.856.27-1.565.914-2.155.717-.657 1.752-.976 2.967-.893 2.101.14 3.316 1.29 3.61 3.424.01.073.02.146.028.22-.12.164-.264.32-.441.452z"/>
                </svg>
              </a>
              <a 
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-amber-800 hover:bg-amber-700 flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-amber-800 hover:bg-amber-700 flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href={socialLinks.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-amber-800 hover:bg-amber-700 flex items-center justify-center transition-colors"
                aria-label="WhatsApp"
              >
                <Phone className="w-5 h-5" />
              </a>
            </div>
            
            <div className="mt-6 bg-amber-800/50 rounded-lg p-4">
              <h4 className="font-semibold mb-2 text-sm">Horário de Atendimento</h4>
              <p className="text-amber-200 text-xs">
                Segunda a Sexta: 9h - 19h<br />
                Sábado: 10h - 16h<br />
                Domingo: Fechado
              </p>
            </div>
          </div>
        </div>

        <Separator className="bg-amber-800 mb-8" />

        {/* Bottom */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-amber-300 text-sm">
          <p>
            © {new Date().getFullYear()} Ser Tão Doce - Waleria Mapeli. Todos os direitos reservados.
          </p>
          <p className="text-center md:text-right">
            Produto artesanal feito sem conservantes artificiais
          </p>
        </div>

        {/* Important notice */}
        <div className="mt-6 bg-amber-800/30 rounded-lg p-4 text-center">
          <p className="text-amber-200 text-sm">
            ⚠️ <strong>ATENÇÃO:</strong> Os produtos podem conter vestígios de oleaginosas e glúten.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
